/* eslint-disable max-lines */
import { ExperienceTemplate } from 'experience-template-renderer-react';

import { SortAndFiltersViewType } from '@shared-ui/retail-sort-and-filter-lodging';

/**
 * the general idea behind the structure of this template is to utilize a root "layout" component, which in this case is
 * is the SRPLayout component. The strategy is to put as much layout related code as possible into that component:
 * UitkLayout*, UitkSpacing, UitkFlex*, etc components. SRPLayout utilizes CSS grid via UitkLayoutGrid/Item components.
 * All SRPLayout does is query the template for "area" (as in CSS Grid template areas: https://developer.mozilla.org/en-US/
 * docs/Web/CSS/grid-template-areas) components, and place their children in an ExperienceTemplateRenderer who itself is
 * placed in one of the CSS grid named areas. These "area" components are not actual declared react components in this app,
 * they are largely used symbolically in this template to organize, hopefully very flat, lists of components to render in
 * that area.
 *
 * If a layout related need can't be applied to all children of an area component, you can utilize the type: UitkComponent
 * in this template to wrap indivdual template components with spacing, padding, etc.
 */
export const fullPageWidthSplitViewTemplate: ExperienceTemplate = {
  id: 'full-page-width-split-view',
  version: '1',
  components: [
    {
      metadata: { id: 'srp-layout-component' },
      type: 'SRPLayout',
      children: [
        {
          metadata: { id: 'header-area-small', name: 'header-area-small' },
          type: 'HeaderAreaSmall',
          children: [
            {
              metadata: {
                id: 'full-width-global-controls-site-header',
              },
              type: 'FullWidthSiteHeader',
            },
            {
              type: 'UitkComponent',
              config: {
                type: 'UitkSpacing',
                margin: {
                  small: { inline: 'three', blockstart: 'three' },
                  medium: { inline: 'six', blockstart: 'four' },
                  large: { inline: 'six', blockstart: 'four' },
                },
              },
              children: [
                {
                  metadata: {
                    id: 'playback',
                  },
                  type: 'Playback',
                },
              ],
            },
            {
              metadata: {
                id: 'filterbar',
              },
              type: 'Filterbar',
              config: {
                view: SortAndFiltersViewType.FILTER_BAR,
              },
            },
          ],
        },
        {
          metadata: { id: 'header-area', name: 'header-area' },
          type: 'HeaderArea',
          children: [
            {
              metadata: {
                id: 'full-width-global-controls-site-header',
              },
              type: 'FullWidthSiteHeader',
            },
            {
              type: 'UitkComponent',
              metadata: {
                id: 'UitkLayoutFlex',
              },
              config: {
                type: 'UitkLayoutFlex',
              },
              children: [
                {
                  metadata: {
                    id: 'uitkLayoutFlexItem',
                  },
                  type: 'UitkComponent',
                  config: {
                    type: 'UitkLayoutFlexItem',
                    grow: 1,
                    maxWidth: 1200,
                  },
                  children: [
                    {
                      type: 'UitkComponent',
                      config: {
                        type: 'UitkSpacing',
                        margin: {
                          small: { inline: 'three', blockstart: 'three' },
                          medium: { inline: 'six', blockstart: 'four' },
                          large: { inline: 'six', blockstart: 'four' },
                        },
                      },
                      children: [
                        {
                          metadata: {
                            id: 'playback',
                          },
                          type: 'Playback',
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              metadata: {
                id: 'filterbar',
              },
              type: 'Filterbar',
              config: {
                view: SortAndFiltersViewType.FILTER_BAR,
              },
            },
          ],
        },
        {
          metadata: { id: 'main-area', name: 'main-area' },
          type: 'MainArea',
          children: [
            {
              metadata: { id: 'search-result-list-padding' },
              type: 'UitkComponent',
              config: {
                type: 'UitkSpacing',
                padding: {
                  small: { inline: 'three' },
                  medium: { inline: 'six' },
                  large: { inline: 'six' },
                  /* eslint-disable camelcase */
                  extra_large: { inline: 'six' },
                },
              },
              children: [
                {
                  metadata: {
                    id: 'split-view-results-header',
                  },
                  type: 'SplitViewResultsHeader',
                  config: {
                    weight: 'regular',
                    size: 200,
                  },
                },
                {
                  metadata: {
                    id: 'search-results',
                  },
                  type: 'SearchResultsWrapper',
                },
              ],
            },
            {
              metadata: {
                id: 'branded-footer',
              },
              type: 'Footer',
            },
          ],
        },
        {
          metadata: { id: 'map-area', name: 'map-area' },
          type: 'MapArea',
          children: [
            {
              metadata: {
                id: 'map',
              },
              type: 'DynamicSearchMapWrapper',
            },
          ],
        },
        {
          metadata: { id: 'ad-area', name: 'ad-area' },
          type: 'AdArea',
          children: [
            {
              metadata: { id: 'meso-scripts' },
              type: 'MesoScripts',
            },
            {
              metadata: { id: 'meso-ad' },
              type: 'MesoSideRail',
            },
          ],
        },
      ],
    },
  ],
};
